/**
 * Exportacao CSV do extrato de conversoes -- gerada no navegador, sem rede.
 *
 * Decisoes:
 *  - separador `;` e BOM UTF-8: e o que o Excel em pt-BR abre direto, com
 *    acento certo e sem juntar tudo numa coluna (a virgula e o decimal aqui);
 *  - exporta o RECORTE FILTRADO inteiro (todas as paginas), ate o teto de
 *    `LIMITE_DA_EXPORTACAO`; acima disso o servidor gera o arquivo;
 *  - "Contato creditado" (toque do motor) e "Fechado por" (vendedora) sao
 *    colunas SEPARADAS: quem conferir a fatura ve os dois fatos lado a lado e
 *    entende por que a venda da vendedora gera comissao (ADR-001 E5);
 *  - estorno e comissao revertida tambem sao colunas separadas: o reembolso
 *    aparece mesmo quando a comissao foi mantida;
 *  - dinheiro passa por `formatBRLFromCents`, o unico lugar que divide por 100;
 *  - nome do lead sai abreviado, como na tela: o extrato e documento
 *    financeiro, nao lista de contatos;
 *  - toda celula que comeca com `=`, `+`, `-`, `@`, tab ou CR ganha um
 *    apostrofo na frente: nome de lead vem do checkout (texto de terceiro) e
 *    "=HYPERLINK(...)" viraria formula ao abrir no Excel (CSV injection).
 */

import { formatBRLFromCents, formatDateTime, formatPercentFromBps, shortenName } from '../../lib/format';
import type { ConversionRow } from './types';
import { CANAL_CONTATO, ENTRADA, ESTORNO, ORIGEM_PAGAMENTO, POLITICA_ESTORNO, STATUS_CONVERSAO, formatMes, formatOffset } from './vocabulario';

const SEPARADOR = ';';
// Montado por codigo, e nao como literal: um U+FEFF invisivel dentro de aspas
// e o tipo de caractere que editor e formatador apagam sem avisar.
const BOM = String.fromCharCode(0xfeff);
const INICIO_PERIGOSO = /^[=+\-@\t\r]/;

const CABECALHO: readonly string[] = [
  'Pagamento em',
  'Empresa',
  'Lead',
  'Oferta',
  'Origem do pagamento',
  'Valor pago',
  '% aplicada',
  'Comissão',
  'Status da comissão',
  'Contato creditado',
  'Passo da régua',
  'Contato em',
  'Fechado por',
  'Entrada',
  'Referência do pedido',
  'Estorno',
  'Estorno em',
  'Valor estornado',
  'Política aplicada ao estorno',
  'Comissão após o estorno',
  'Mês da fatura',
  'Id',
];

function celula(valor: string): string {
  const seguro = INICIO_PERIGOSO.test(valor) ? `'${valor}` : valor;
  const precisaAspas = seguro.includes(SEPARADOR) || seguro.includes('"') || seguro.includes('\n') || seguro.includes('\r');
  return precisaAspas ? `"${seguro.replace(/"/g, '""')}"` : seguro;
}

function linhaCsv(linha: ConversionRow): readonly string[] {
  const vendedora = linha.closedBy;
  const estorno = linha.refund;
  return [
    formatDateTime(linha.paidAt),
    linha.companyName,
    shortenName(linha.leadName),
    linha.offerName,
    ORIGEM_PAGAMENTO[linha.source],
    formatBRLFromCents(linha.paidAmountCents),
    formatPercentFromBps(linha.feeBpsApplied),
    formatBRLFromCents(linha.commissionCents),
    STATUS_CONVERSAO[linha.status].label,
    CANAL_CONTATO[linha.creditedContact.channel].label,
    formatOffset(linha.creditedContact.offsetMinutes),
    formatDateTime(linha.creditedContact.at),
    vendedora === null ? 'Direto no checkout' : shortenName(vendedora.sellerName),
    // Sem vendedora, a entrada sai da origem do pagamento: registro manual nunca e "Webhook".
    vendedora === null ? ENTRADA[linha.source === 'manual' ? 'venda_assistida' : 'webhook'].label : ENTRADA[vendedora.via].label,
    vendedora?.orderRef ?? '',
    estorno === null ? '' : ESTORNO[estorno.kind].label,
    estorno === null ? '' : formatDateTime(estorno.at),
    estorno === null ? '' : formatBRLFromCents(estorno.amountCents),
    estorno === null ? '' : POLITICA_ESTORNO[estorno.policyApplied].label,
    estorno === null ? '' : estorno.reversed ? 'Revertida' : 'Mantida',
    formatMes(linha.invoiceMonth),
    linha.id,
  ];
}

export function conversoesParaCsv(linhas: readonly ConversionRow[]): string {
  const corpo = linhas.map((linha) => linhaCsv(linha).map(celula).join(SEPARADOR));
  return `${BOM}${[CABECALHO.map(celula).join(SEPARADOR), ...corpo].join('\r\n')}\r\n`;
}

/** Dispara o download de um texto como arquivo. O objeto URL e liberado logo depois do clique. */
export function baixarArquivoTexto(nome: string, conteudo: string, tipo = 'text/csv;charset=utf-8'): void {
  const blob = new Blob([conteudo], { type: tipo });
  const url = URL.createObjectURL(blob);
  const ancora = document.createElement('a');
  ancora.href = url;
  ancora.download = nome;
  ancora.rel = 'noopener';
  document.body.appendChild(ancora);
  ancora.click();
  document.body.removeChild(ancora);
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** "comissoes-todas-as-empresas-2026-09-24.csv": sem acento, sem espaco, data no fim. */
export function nomeDoArquivoCsv(escopo: string, data: Date): string {
  const y = data.getFullYear();
  const m = String(data.getMonth() + 1).padStart(2, '0');
  const d = String(data.getDate()).padStart(2, '0');
  const slug = escopo
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `comissoes-${slug === '' ? 'empresas' : slug}-${y}-${m}-${d}.csv`;
}
